'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackPhoneClick, trackEmailClick } from '@/lib/seo/conversionTracking';

// Simple client-side validation functions
const sanitizeInput = (input: string): string => {
  if (!input) return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= 254;
};

const validatePhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false;
  const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
  return phoneRegex.test(phone);
};

const validateName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false;
  const nameRegex = /^[a-zA-Z\s\-']{2,50}$/;
  return nameRegex.test(name.trim());
};

interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

interface LeadCaptureFormProps {
  fields: FormField[];
  onSubmit: (data: Record<string, string>) => Promise<void>;
  submitButtonText?: string;
  title?: string;
  description?: string;
}

const LeadCaptureForm = ({
  fields,
  onSubmit,
  submitButtonText = 'Submit Inquiry',
  title,
  description,
}: LeadCaptureFormProps) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // For textarea, preserve all spaces and allow normal text input
    const sanitizedValue = e.target.tagName === 'TEXTAREA' 
      ? value.slice(0, 2000)
      : sanitizeInput(value);
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      const value = formData[field.name];
      
      if (field.required && !value?.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      }

      if (field.type === 'email' && value) {
        if (!validateEmail(value)) {
          newErrors[field.name] = 'Please enter a valid email address';
        }
      }

      if (field.type === 'tel' && value) {
        if (!validatePhone(value)) {
          newErrors[field.name] = 'Please enter a valid phone number';
        }
      }
      
      if (field.type === 'text' && field.name.toLowerCase().includes('name') && value) {
        if (!validateName(value)) {
          newErrors[field.name] = 'Please enter a valid name (letters only)';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      await onSubmit(formData);
      setShowSuccessModal(true);
      setFormData({});
      
      // Auto-close modal after 5 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 5000);
    } catch (error: any) {
      console.error('Form submission error:', error);
      const errorMessage = error?.message || 'Failed to submit form. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Success Modal Popup */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative mx-4 w-full max-w-md rounded-2xl bg-card p-8 shadow-2xl border-2 border-success/30 animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <Icon name="XMarkIcon" size={24} variant="outline" />
            </button>
            
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-success/10 p-4">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
              </div>
              
              <h3 className="mb-2 font-heading text-2xl font-bold text-foreground">
                Message Sent Successfully!
              </h3>
              
              <p className="mb-6 text-muted-foreground">
                Thank you for reaching out! We've received your inquiry and will get back to you within 24 hours.
              </p>
              
              <button
                onClick={() => setShowSuccessModal(false)}
                className="rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-smooth hover:bg-primary/90 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-card p-8 shadow-warm-md border-2 border-accent/20">
        {/* Urgency Banner */}
        <div className="mb-6 rounded-lg bg-accent/10 p-4 border border-accent/30">
          <div className="flex items-center gap-3">
            <Icon name="SparklesIcon" size={24} variant="solid" className="text-accent" />
            <div>
              <p className="font-semibold text-foreground">
                🎁 Reserve Today & Get $200 OFF + Free Starter Kit
              </p>
              <p className="text-sm text-muted-foreground">
                Limited offer - Only 3 puppies remaining this month
              </p>
            </div>
          </div>
        </div>

        {/* Contact Options with Tracking */}
        <div className="mb-6 flex flex-wrap gap-4">
          <a
            href="tel:+15551234567"
            onClick={() => trackPhoneClick()}
            className="flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            <Icon name="PhoneIcon" size={18} variant="solid" />
            Call Now: (555) 123-4567
          </a>
          <a
            href="mailto:contact@luminexlabradors.com"
            onClick={() => trackEmailClick()}
            className="flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
          >
            <Icon name="EnvelopeIcon" size={18} variant="solid" />
            Email Us
          </a>
        </div>

        {title && (
          <h3 className="mb-2 font-heading text-2xl font-semibold text-foreground">
            {title}
          </h3>
        )}
        {description && (
          <p className="mb-6 text-muted-foreground">
            {description}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            {fields.map((field) => (
              <div
                key={field.name}
                className={field.type === 'textarea' ? 'md:col-span-2' : ''}
              >
                <label
                  htmlFor={field.name}
                  className="mb-2 block text-sm font-medium text-foreground"
                >
                  {field.label}
                  {field.required && <span className="ml-1 text-error">*</span>}
                </label>

                {field.type === 'textarea' ? (
                  <textarea
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    rows={4}
                    className={`w-full rounded-md border bg-input px-4 py-3 text-foreground transition-smooth placeholder:text-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 resize-y ${
                      errors[field.name]
                        ? 'border-error' :'border-border hover:border-primary/30'
                    }`}
                  />
                ) : field.type === 'select' ? (
                  <select
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    className={`w-full rounded-md border bg-input px-4 py-3 text-foreground transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 ${
                      errors[field.name]
                        ? 'border-error' :'border-border hover:border-primary/30'
                    }`}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options?.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    className={`w-full rounded-md border bg-input px-4 py-3 text-foreground transition-smooth placeholder:text-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 ${
                      errors[field.name]
                        ? 'border-error' :'border-border hover:border-primary/30'
                    }`}
                  />
                )}

                {errors[field.name] && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-error">
                    <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {errors.submit && (
            <div className="flex items-start gap-3 rounded-md bg-error/10 p-4 text-error">
              <Icon name="ExclamationTriangleIcon" size={20} variant="solid" />
              <p className="text-sm font-medium">{errors.submit}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-accent px-7 font-medium text-accent-foreground shadow-warm transition-smooth hover:scale-[0.97] hover:shadow-warm-md focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                {submitButtonText}
                <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
              </>
            )}
          </button>

          {/* Trust Signals Below Form */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 border-t border-border pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="LockClosedIcon" size={16} variant="solid" className="text-success" />
              <span>Secure & Private</span>
            </div>
            <div className="h-4 w-px bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="ClockIcon" size={16} variant="solid" className="text-primary" />
              <span>24-Hour Response</span>
            </div>
            <div className="h-4 w-px bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-accent" />
              <span>No Obligation</span>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default LeadCaptureForm;